import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  amountPlaceholder,
  BoundFigures,
  boundsPairReady,
  bridgeRateLine,
  displayCurrency,
  feeLine,
  fillBoundTemplate,
  formatBoundAmount,
  formatBridgeRate,
  formatMoney,
  MAX_GROSS_SEND_USD,
  mergeEstimateBounds,
  modeLabel,
  quoteExceedsGrossSendCap,
  showsSourceFxLine,
  typedAmountBlocked,
  youSendHelper,
  youSendMaximumSentence,
} from './calculatorDisplay';
import {
  fetchRemittanceChannels,
  fetchRemittanceDestinations,
  fetchRemittanceEstimate,
  fetchRemittanceRailBounds,
  fetchRemittanceSources,
  RemittanceApiError,
  RemittanceChannel,
  RemittanceDestination,
  RemittanceEstimate,
  RemittanceSource,
} from '../services/remittanceCalculatorApi';
const PREFETCH_LIMIT = 8;

type Direction = 'recipient' | 'sender';

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: 6,
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '0.01em',
  color: 'var(--color-text-secondary)',
};

const controlStyle: React.CSSProperties = {
  width: '100%',
  minWidth: 0,
  minHeight: 48,
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid var(--color-border-primary)',
  background: 'var(--white)',
  color: 'var(--color-text-primary)',
  font: 'inherit',
  boxSizing: 'border-box',
};

const wrapStyle: React.CSSProperties = { overflowWrap: 'anywhere' };

type Choice = { value: string; label: string };

function Spinner() {
  return <span className="calculator-spinner" aria-hidden="true" />;
}

function ChoiceField({
  label,
  value,
  placeholder,
  options,
  disabled,
  busy,
  includeBlank = true,
  onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  options: Choice[];
  disabled?: boolean;
  busy?: boolean;
  includeBlank?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((option) => option.value === value);
  const locked = Boolean(disabled || busy);

  useEffect(() => {
    if (locked) setOpen(false);
  }, [locked]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function choose(next: string) {
    onChange(next);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative mb-4 min-w-0">
      <span style={labelStyle}>{label}</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={locked}
        onClick={() => setOpen((current) => !current)}
        style={{
          ...controlStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          textAlign: 'start',
          cursor: locked ? 'default' : 'pointer',
        }}
      >
        <span className="min-w-0" style={{ overflowWrap: 'anywhere', color: selected ? 'inherit' : 'var(--color-text-secondary)' }}>
          {selected ? selected.label : placeholder}
        </span>
        {busy ? <Spinner /> : null}
      </button>
      {open && !locked ? (
        <ul
          role="listbox"
          aria-label={label}
          className="absolute left-0 right-0 z-30 mt-1 max-h-64 min-w-0 overflow-auto"
          style={{
            background: 'var(--white)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(24, 133, 154, 0.12)',
            padding: 6,
            margin: 0,
            listStyle: 'none',
          }}
        >
          {includeBlank ? (
            <li role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === ''}
                style={optionStyle(value === '')}
                onClick={() => choose('')}
              >
                {placeholder}
              </button>
            </li>
          ) : null}
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                style={optionStyle(option.value === value)}
                onClick={() => choose(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function optionStyle(selected: boolean): React.CSSProperties {
  return {
    width: '100%',
    minHeight: 44,
    padding: '10px 12px',
    border: 0,
    borderRadius: 10,
    background: selected ? 'rgba(24, 133, 154, 0.12)' : 'transparent',
    color: selected ? 'var(--teal-deep)' : 'var(--color-text-primary)',
    font: 'inherit',
    fontWeight: selected ? 700 : 500,
    textAlign: 'start',
    cursor: 'pointer',
    overflowWrap: 'anywhere',
  };
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    minWidth: 0,
    minHeight: 48,
    padding: '10px 12px',
    border: 0,
    borderBottom: active ? '3px solid var(--teal)' : '3px solid transparent',
    background: 'transparent',
    color: active ? 'var(--teal-deep)' : 'var(--color-text-secondary)',
    font: 'inherit',
    fontWeight: active ? 700 : 500,
    textAlign: 'center',
    cursor: 'pointer',
    overflowWrap: 'anywhere',
  };
}

const amountBoxStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  minHeight: 48,
  borderRadius: 12,
  border: '1px solid var(--color-border-primary)',
  background: 'var(--white)',
};

const amountInputStyle: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 48,
  padding: '12px 8px 12px 14px',
  border: 0,
  background: 'transparent',
  color: 'var(--color-text-primary)',
  font: 'inherit',
  boxSizing: 'border-box',
};

const currencySuffixStyle: React.CSSProperties = {
  flex: '0 0 auto',
  paddingRight: 14,
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  whiteSpace: 'nowrap',
};

const CalculatorCard: React.FC = () => {
  const { t } = useTranslation();
  const [sources, setSources] = useState<RemittanceSource[]>([]);
  const [destinations, setDestinations] = useState<RemittanceDestination[]>([]);
  const [sourceCode, setSourceCode] = useState('');
  const [destinationCode, setDestinationCode] = useState('');
  const [channels, setChannels] = useState<RemittanceChannel[]>([]);
  const [channelsFailed, setChannelsFailed] = useState(false);
  const [channelsSettled, setChannelsSettled] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [mode, setMode] = useState('');
  const [direction, setDirection] = useState<Direction>('recipient');
  const [amount, setAmount] = useState('');
  const [estimate, setEstimate] = useState<RemittanceEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [entryPrompt, setEntryPrompt] = useState(false);
  const [failure, setFailure] = useState<'none' | 'unavailable' | 'cap' | 'minimum'>('none');
  const [minimumMessage, setMinimumMessage] = useState<string | null>(null);
  const [bounds, setBounds] = useState<BoundFigures | null>(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    if (!entryPrompt) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setEntryPrompt(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [entryPrompt]);

  useEffect(() => {
    const controller = new AbortController();
    let failed = false;
    const sourcesPromise = fetchRemittanceSources(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setSources(rows ?? []);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        failed = true;
        setSources([]);
      });
    const destinationsPromise = fetchRemittanceDestinations(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setDestinations(rows ?? []);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        failed = true;
        setDestinations([]);
      });
    Promise.all([sourcesPromise, destinationsPromise]).then(() => {
      if (!controller.signal.aborted) setCatalogFailed(failed);
    });
    return () => controller.abort();
  }, []);

  const sourceCorridor = sources.find((source) => source.countryCode === sourceCode)?.corridorCode ?? '';

  useEffect(() => {
    if (!sourceCorridor) {
      setBounds(null);
      return undefined;
    }
    setBounds(null);
    const controller = new AbortController();
    fetchRemittanceRailBounds(sourceCorridor, controller.signal)
      .then((row) => {
        if (controller.signal.aborted || !row) return;
        setBounds({
          typedCurrency: row.typedCurrency,
          minTypedAmount: row.minTypedAmount,
          maxTypedAmount: row.maxTypedAmount,
          minReceiveUsd: row.minReceiveUsd,
          maxReceiveUsd: row.maxReceiveUsd,
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setBounds(null);
        }
      });
    return () => controller.abort();
  }, [sourceCorridor]);

  useEffect(() => {
    if (catalogFailed || destinations.length === 0) return undefined;
    const controller = new AbortController();
    destinations.slice(0, PREFETCH_LIMIT).forEach((destination) => {
      fetchRemittanceChannels(destination.countryCode, controller.signal).catch(() => undefined);
    });
    return () => controller.abort();
  }, [catalogFailed, destinations]);

  useEffect(() => {
    setChannels([]);
    setChannelsSettled(false);
    setChannelId('');
    setMode('');
    if (!destinationCode) {
      setChannelsFailed(false);
      return undefined;
    }
    const controller = new AbortController();
    setChannelsFailed(false);
    fetchRemittanceChannels(destinationCode, controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;
        const list = rows ?? [];
        setChannels(list);
        setChannelsSettled(true);
        if (list.length === 1) {
          const only = list[0];
          setChannelId(String(only.channelTypeId));
          const modes = only.allowedReceiveDeliveryModes ?? [];
          const usd = modes.find((item) => item.toUpperCase() === 'USD');
          setMode(usd ?? modes[0] ?? '');
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setChannels([]);
          setChannelsFailed(true);
          setChannelsSettled(true);
        }
      });
    return () => controller.abort();
  }, [destinationCode]);

  const selectedChannel = channels.find((channel) => String(channel.channelTypeId) === channelId);
  const selectedSource = sources.find((source) => source.countryCode === sourceCode);
  const modes = selectedChannel?.allowedReceiveDeliveryModes ?? [];
  const parsed = amount.trim() === '' ? null : Number(amount);
  const amountMissing = parsed == null || Number.isNaN(parsed);
  const pairReady = boundsPairReady(direction, bounds);
  const amountOutOfRange =
    !amountMissing && typedAmountBlocked(direction, parsed as number, bounds);
  const showNoChannel =
    Boolean(destinationCode) && channelsSettled && (channelsFailed || channels.length === 0);
  const channelBusy = Boolean(destinationCode) && !channelsSettled && !showNoChannel;
  const canEstimate =
    Boolean(sourceCode) &&
    Boolean(destinationCode) &&
    Boolean(selectedChannel) &&
    Boolean(mode) &&
    pairReady &&
    !amountMissing &&
    !amountOutOfRange &&
    !showNoChannel;

  const typedCurrency = bounds?.typedCurrency || displayCurrency(selectedSource?.sourceCurrency) || '';
  const boundsHelper =
    direction === 'sender' && bounds?.minTypedAmount != null && bounds.maxTypedAmount != null
      ? youSendHelper(
          t('calculator.sendBounds'),
          t('calculator.sendBoundsRate'),
          bounds.minTypedAmount,
          bounds.maxTypedAmount,
          typedCurrency,
        )
      : direction === 'recipient' && bounds?.minReceiveUsd != null && bounds.maxReceiveUsd != null
        ? fillBoundTemplate(t('calculator.receiveBounds'), {
            min: formatBoundAmount(bounds.minReceiveUsd),
            max: formatBoundAmount(bounds.maxReceiveUsd),
          })
        : '';
  const rangeMessage = (() => {
    if (amountMissing || parsed == null) return '';
    if (direction === 'sender') {
      if (bounds?.minTypedAmount != null && parsed < bounds.minTypedAmount) {
        return fillBoundTemplate(t('calculator.sendBelow'), {
          min: formatBoundAmount(bounds.minTypedAmount),
          currency: typedCurrency,
        });
      }
      if (bounds?.maxTypedAmount != null && parsed > bounds.maxTypedAmount) {
        return youSendMaximumSentence(
          t('calculator.sendAbove'),
          t('calculator.sendAboveRate'),
          bounds.maxTypedAmount,
          typedCurrency,
        );
      }
      return '';
    }
    if (bounds?.minReceiveUsd != null && parsed < bounds.minReceiveUsd) {
      return fillBoundTemplate(t('calculator.receiveBelow'), {
        min: formatBoundAmount(bounds.minReceiveUsd),
      });
    }
    if (bounds?.maxReceiveUsd != null && parsed > bounds.maxReceiveUsd) {
      return fillBoundTemplate(t('calculator.receiveAbove'), {
        max: formatBoundAmount(bounds.maxReceiveUsd),
      });
    }
    if ((bounds?.maxReceiveUsd == null) && parsed > MAX_GROSS_SEND_USD) {
      return t('calculator.maxSend');
    }
    return '';
  })();
  const capMessage =
    bounds?.maxTypedAmount != null
      ? youSendMaximumSentence(
          t('calculator.sendAbove'),
          t('calculator.sendAboveRate'),
          bounds.maxTypedAmount,
          typedCurrency,
        )
      : t('calculator.maxSend');
  const minimumCopy =
    bounds?.minTypedAmount != null
      ? fillBoundTemplate(t('calculator.sendBelow'), {
          min: formatBoundAmount(bounds.minTypedAmount),
          currency: typedCurrency,
        })
      : minimumMessage;

  useEffect(() => {
    const current = ++generation.current;
    if (!canEstimate || !selectedChannel) {
      setEstimate(null);
      setFailure('none');
      setMinimumMessage(null);
      setLoading(false);
      return undefined;
    }
    setFailure('none');
    setMinimumMessage(null);
    const controller = new AbortController();
    const handle = window.setTimeout(() => {
      if (generation.current !== current) return;
      setLoading(true);
      const query = {
        sourceCountryCode: sourceCode,
        destinationCountryCode: destinationCode,
        receiveDeliveryMode: mode,
        disbursementChannelId: selectedChannel.channelTypeId,
        ...(direction === 'sender' ? { sendAmount: parsed as number } : { targetAmountUsd: parsed as number }),
      };
      fetchRemittanceEstimate(query, controller.signal)
        .then((row) => {
          if (generation.current !== current) return;
          if (quoteExceedsGrossSendCap(row)) {
            setEstimate(null);
            setFailure('cap');
            return;
          }
          setEstimate(row);
          setBounds((current) => mergeEstimateBounds(current, row));
          setFailure('none');
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted || generation.current !== current) return;
          setEstimate(null);
          const apiError = err instanceof RemittanceApiError ? err : null;
          if (apiError?.errorCode === 'REMIT_001') {
            setFailure('cap');
            setMinimumMessage(null);
          } else if (apiError?.message?.startsWith('The amount you send must be at least ')) {
            setFailure('minimum');
            setMinimumMessage(apiError.message);
          } else {
            setFailure('unavailable');
            setMinimumMessage(null);
          }
        })
        .finally(() => {
          if (generation.current === current) setLoading(false);
        });
    }, 700);
    return () => {
      window.clearTimeout(handle);
      controller.abort();
    };
  }, [canEstimate, sourceCode, destinationCode, mode, direction, parsed, selectedChannel]);

  function onSourceChange(value: string) {
    setSourceCode(value);
    setFailure('none');
  }

  function onDestinationChange(value: string) {
    setDestinationCode(value);
    setChannels([]);
    setChannelsSettled(false);
    setChannelsFailed(false);
    setChannelId('');
    setMode('');
    setFailure('none');
  }

  function onDirectionChange(next: Direction) {
    if (next === direction || loading) return;
    generation.current += 1;
    setDirection(next);
    setAmount('');
    setEstimate(null);
    setFailure('none');
    setMinimumMessage(null);
    setLoading(false);
  }

  function onChannelChange(value: string) {
    setChannelId(value);
    const next = channels.find((channel) => String(channel.channelTypeId) === value);
    const nextModes = next?.allowedReceiveDeliveryModes ?? [];
    const usd = nextModes.find((item) => item.toUpperCase() === 'USD');
    setMode(usd ?? nextModes[0] ?? '');
    setFailure('none');
  }

  const localCode = estimate?.localCurrencyCode;
  const recipientAmount =
    mode.toUpperCase() === 'LOCAL'
      ? formatMoney(estimate?.netLocalAmount ?? estimate?.grossLocalAmount, localCode)
      : formatMoney(estimate?.targetAmountUsd, 'USD');
  const feeText = estimate
    ? feeLine(
        t('calculator.feeOfArriving'),
        formatMoney(estimate.lulpayFeeAmount, estimate.sourceCurrencyCode),
        estimate.lulpayFeePercent,
      )
    : '';
  const rateText = formatBridgeRate(estimate?.sourceCurrencyCode, estimate?.bridgeBuyRateToUsd);
  const fxLine =
    estimate && showsSourceFxLine(estimate.sourceCurrencyCode) && rateText
      ? bridgeRateLine(
          t('calculator.bridgeRate'),
          displayCurrency(estimate.sourceCurrencyCode),
          rateText,
        )
      : '';
  const youPay = formatMoney(estimate?.requiredSendAmount, estimate?.sourceCurrencyCode);
  const sendCurrency = displayCurrency(typedCurrency || selectedSource?.sourceCurrency);
  const entryCurrency = direction === 'sender' ? sendCurrency : 'USD';
  const entryReady = Boolean(sourceCode && destinationCode && channelId && mode);
  const showResult = !loading && (Boolean(estimate) || failure !== 'none');

  return (
    <div id="calculator" className="hero-calculator mx-auto w-full min-w-0" style={{ maxWidth: 420 }}>
      <div
        className="relative min-w-0"
        aria-busy={loading || channelBusy}
        style={{
          background: 'var(--white)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: 24,
          padding: 24,
          boxShadow: 'var(--shadow-raised)',
        }}
      >
        {entryPrompt ? (
          <div
            className="absolute inset-0 z-20 flex items-center p-4"
            style={{ background: 'rgba(39, 39, 39, 0.28)', borderRadius: 24 }}
            onClick={() => setEntryPrompt(false)}
          >
            <div
              role="dialog"
              aria-modal="false"
              aria-labelledby="calculator-fields-title"
              className="min-w-0"
              style={{
                width: '100%',
                background: 'var(--white)',
                borderRadius: 16,
                borderTop: '4px solid var(--teal)',
                padding: 20,
                boxShadow: '0 12px 40px rgba(24, 133, 154, 0.18)',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <p id="calculator-fields-title" className="min-w-0" style={{ ...wrapStyle, fontWeight: 700, color: 'var(--teal-deep)' }}>
                {t('calculator.chooseFieldsFirst')}
              </p>
              <button
                type="button"
                className="mt-4"
                style={{
                  minHeight: 44,
                  padding: '10px 18px',
                  border: 0,
                  borderRadius: 12,
                  background: 'var(--teal)',
                  color: 'var(--white)',
                  font: 'inherit',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
                autoFocus
                onClick={() => setEntryPrompt(false)}
              >
                {t('calculator.dismiss')}
              </button>
            </div>
          </div>
        ) : null}
        {catalogFailed ? (
          <p className="mb-4 min-w-0" style={{ color: 'var(--teal-deep)', overflowWrap: 'anywhere' }}>
            {t('calculator.unavailable')}
          </p>
        ) : null}

        <ChoiceField
          label={t('calculator.chooseSource')}
          value={sourceCode}
          placeholder={t('calculator.chooseSource')}
          disabled={loading}
          options={sources.map((source) => ({
            value: source.countryCode,
            label: source.displayName,
          }))}
          onChange={onSourceChange}
        />

        <ChoiceField
          label={t('calculator.chooseDestination')}
          value={destinationCode}
          placeholder={t('calculator.chooseDestination')}
          disabled={loading}
          options={destinations.map((destination) => ({
            value: destination.countryCode,
            label: destination.displayName,
          }))}
          onChange={onDestinationChange}
        />

        <ChoiceField
          label={t('receive.eyebrow')}
          value={channelId}
          placeholder={t('receive.eyebrow')}
          disabled={!destinationCode || showNoChannel || loading}
          busy={channelBusy}
          options={channels.map((channel) => ({
            value: String(channel.channelTypeId),
            label: channel.displayName,
          }))}
          onChange={onChannelChange}
        />
        {showNoChannel ? (
          <p className="mb-4 min-w-0" style={wrapStyle}>
            {t('calculator.noChannel')}
          </p>
        ) : null}

        {modes.length > 1 ? (
          <ChoiceField
            label={t('calculator.localCurrency')}
            value={mode}
            placeholder={t('calculator.localCurrency')}
            includeBlank={false}
            disabled={loading}
            options={modes.map((item) => ({
              value: item,
              label: modeLabel(item, localCode, t('calculator.localCurrency')),
            }))}
            onChange={(next) => {
              setMode(next);
              setFailure('none');
            }}
          />
        ) : null}

        <div className="mb-4 min-w-0" role="tablist" aria-label={t('calculator.sendAmount')}>
          <div className="grid grid-cols-2" style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
            <button
              type="button"
              role="tab"
              aria-selected={direction === 'sender'}
              disabled={loading}
              style={{ ...tabStyle(direction === 'sender'), cursor: loading ? 'default' : 'pointer' }}
              onClick={() => onDirectionChange('sender')}
            >
              {t('calculator.youSend')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={direction === 'recipient'}
              disabled={loading}
              style={{ ...tabStyle(direction === 'recipient'), cursor: loading ? 'default' : 'pointer' }}
              onClick={() => onDirectionChange('recipient')}
            >
              {t('calculator.recipientGets')}
            </button>
          </div>
        </div>

        <label className="mb-4 block min-w-0">
          <span style={labelStyle}>
            {direction === 'sender' ? t('calculator.youSend') : t('calculator.recipientGets')}
          </span>
          <div
            style={{
              ...amountBoxStyle,
              background: entryReady && !loading ? 'var(--white)' : 'rgba(24, 133, 154, 0.04)',
            }}
            onMouseDown={(event) => {
              if (entryReady || loading) return;
              event.preventDefault();
              setEntryPrompt(true);
            }}
          >
            <input
              className="min-w-0"
              style={amountInputStyle}
              inputMode="decimal"
              placeholder={entryReady ? amountPlaceholder(direction, bounds) : ''}
              value={amount}
              readOnly={!entryReady}
              disabled={loading}
              aria-label={
                direction === 'sender' ? t('calculator.youSend') : t('calculator.recipientGets')
              }
              onMouseDown={(event) => {
                if (entryReady || loading) return;
                event.preventDefault();
                setEntryPrompt(true);
              }}
              onFocus={() => {
                if (!entryReady) setEntryPrompt(true);
              }}
              onChange={(event) => {
                if (!entryReady || loading) return;
                setAmount(event.target.value);
                setFailure('none');
              }}
            />
            {entryCurrency ? (
              <span style={currencySuffixStyle}>{entryCurrency}</span>
            ) : null}
          </div>
        </label>
        {loading ? (
          <p className="mb-3 flex min-w-0 items-center gap-2" role="status" style={{ color: 'var(--teal-deep)' }}>
            <Spinner />
            <span className="min-w-0" style={wrapStyle}>{t('calculator.calculating')}</span>
          </p>
        ) : null}
        {boundsHelper ? (
          <p className="mb-2 min-w-0" style={{ ...wrapStyle, color: 'var(--color-text-secondary)' }}>
            {boundsHelper}
          </p>
        ) : null}
        {amountOutOfRange && rangeMessage ? (
          <p className="mb-4 min-w-0" style={{ ...wrapStyle, color: 'var(--error)' }}>
            {rangeMessage}
          </p>
        ) : null}

        {showResult ? (
          <div
            className="relative min-w-0"
            style={{
              padding: 16,
              borderRadius: 16,
              background: 'rgba(24, 133, 154, 0.06)',
            }}
          >
            {failure !== 'none' ? (
              <p className="min-w-0" style={wrapStyle}>
                {failure === 'cap'
                  ? capMessage
                  : failure === 'minimum' && minimumCopy
                    ? minimumCopy
                    : t('calculator.unavailable')}
              </p>
            ) : null}
            {estimate ? (
              <div className="min-w-0">
                <p className="min-w-0" style={{ ...wrapStyle, fontWeight: 700 }}>
                  {t('calculator.youSendToday')}
                </p>
                <p className="min-w-0" style={{ ...wrapStyle, fontSize: '1.5rem', fontWeight: 700 }}>
                  {youPay}
                </p>
                <div className="my-3" style={{ height: 1, background: 'var(--color-border-primary)' }} />
                <p className="min-w-0 text-sm" style={{ ...wrapStyle, color: 'var(--color-text-primary)' }}>
                  {t('calculator.recipientGets')}
                </p>
                <p className="min-w-0" style={{ ...wrapStyle, fontSize: '1.25rem', fontWeight: 700 }}>
                  {recipientAmount}
                </p>
                <p className="mt-3 min-w-0" style={wrapStyle}>
                  {feeText}
                </p>
                <p className="mt-1 min-w-0 text-sm" style={{ ...wrapStyle, color: 'var(--color-text-primary)' }}>
                  {t('calculator.feeBasisNote')}
                </p>
                {fxLine ? (
                  <p className="mt-2 min-w-0" style={wrapStyle}>
                    {fxLine}
                  </p>
                ) : null}
                <p className="mt-3 min-w-0 text-sm" style={{ ...wrapStyle, color: 'var(--color-text-primary)' }}>
                  {t('calculator.indicative')}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CalculatorCard;
